import { useState, useCallback, useEffect, useRef } from 'react';
import base32 from 'hi-base32';
import { useDispatch, useSelector } from 'react-redux';
import { purchase as purchaseApi } from 'js-api-client';
import usePurchase from 'hooks/store/usePurchase';
import { setError } from '../../actions';
import { setEnableMonthlySelectedPlan, resetInstallmentsPlan } from '../../actions/purchase';
import adyen from '../../../payments/core/engines/adyen';
import { cardPaymentAttempt } from '../../actions/payment';

function useAdyen(adyenElement, showAdyenForm) {
  const dispatch = useDispatch();
  const requestedPayment = useRef(false);

  const { env, features } = useSelector((state) => state.whitelabelConfig);
  const { currency } = env;

  const {
    token: purchaseToken,
    total: purchaseTotal,
    selectedPaymentOption,
    monthlySelectedPlan,
  } = usePurchase();

  const [firstDigits, setFirstDigits] = useState(null);
  const [lastDigits, setLastDigits] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [holderNameValid, setHolderNameValid] = useState(true);
  const isMountedRef = useRef(true);
  const cardComponentRef = useRef(null);

  // Cleanup to prevent state updates on unmounted component and reset installments state
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Reset installments state when component unmounts
      dispatch(resetInstallmentsPlan());
    };
  }, [dispatch]);

  function onBinValue({ binValue }) {
    setFirstDigits(binValue);

    // Only validate BIN if the feature flag is enabled
    // This prevents unnecessary API calls for tenants that don't use this validation
    const shouldValidateBin = features.BIN_VALIDATION_FOR_INSTALLMENTS_ENABLED;
    if (!shouldValidateBin) {
      return;
    }

    const binLength = binValue?.length;
    if (binLength === 6) {
      purchaseApi
        .validateInstallments({ bin: binValue })
        .then((response) => {
          if (isMountedRef.current) {
            dispatch(setEnableMonthlySelectedPlan(response.eligible ?? false));
          }
        })
        .catch((error) => {
          console.error('Error validating installments:', error);
          if (isMountedRef.current) {
            dispatch(setEnableMonthlySelectedPlan(false));
            dispatch(resetInstallmentsPlan());
          }
        });
    } else if (isMountedRef.current) {
      dispatch(setEnableMonthlySelectedPlan(false));
      dispatch(resetInstallmentsPlan());
    }
  }

  function onFieldValid({ fieldType, endDigits }) {
    if (fieldType !== 'encryptedCardNumber') return;
    setLastDigits(endDigits);
  }

  function onBrand({ brand }) {
    setCardBrand(brand);
  }

  const validateHolderName = useCallback((value) => {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s]{1,100}$/;
    return nameRegex.test(value);
  }, []);

  const ADYEN_CLASSES = {
    input: {
      error: 'adyen-checkout__input--error',
      valid: 'adyen-checkout__input--valid',
    },
    validation: {
      wrapper: 'adyen-checkout-input__inline-validation',
      valid: 'adyen-checkout-input__inline-validation--valid',
      invalid: 'adyen-checkout-input__inline-validation--invalid',
    },
    icon: 'adyen-checkout__icon',
  };

  const ICONS = {
    error: {
      alt: 'field_error',
      src: 'https://checkoutshopper-test.adyen.com/checkoutshopper/images/components/field_error.svg',
    },
    valid: {
      alt: 'checkmark',
      src: 'https://checkoutshopper-test.adyen.com/checkoutshopper/images/components/checkmark.svg',
    },
  };

  /**
   * Validates the cardholder name and manages error states in the UI.
   * This function is memoized using useCallback to prevent unnecessary re-renders.
   *
   * @param {string} holderName - The cardholder name to validate
   * @returns {void}
   *
   * The function:
   * - Returns early if holderName is empty
   * - Validates the name using validateHolderName regex
   * - Replaces clases depending on the validation result
   */
  const handleHolderNameValidation = useCallback(
    (holderName) => {
      if (!holderName) return;

      const isValid = validateHolderName(holderName);
      const holderNameInput = document.querySelector('input[id*=adyen-checkout-holderName]');
      const validationWrapper = holderNameInput?.parentElement?.querySelector(
        `.${ADYEN_CLASSES.validation.wrapper}`,
      );

      if (!holderNameInput || !validationWrapper) return;

      /**
       * Updates the validation state UI for the cardholder name input
       * @param {boolean} isValidState - Whether the input is valid
       */
      const updateValidationState = (isValidState) => {
        holderNameInput.style.color = isValidState ? '' : '#ff040d';
        holderNameInput.classList.toggle(ADYEN_CLASSES.input.error, !isValidState);
        holderNameInput.classList.toggle(ADYEN_CLASSES.input.valid, isValidState);

        // Hide all existing icons
        Array.from(validationWrapper.children).forEach((child) => {
          child.classList.add('hidden');
        });

        // Update validation wrapper classes
        validationWrapper.classList.toggle(ADYEN_CLASSES.validation.invalid, !isValidState);
        validationWrapper.classList.toggle(ADYEN_CLASSES.validation.valid, isValidState);

        // Show appropriate icon
        const iconConfig = isValidState ? ICONS.valid : ICONS.error;
        let icon = validationWrapper.querySelector(`img[alt="${iconConfig.alt}"]`);

        if (!icon) {
          icon = document.createElement('img');
          icon.className = ADYEN_CLASSES.icon;
          icon.alt = iconConfig.alt;
          icon.src = iconConfig.src;
          validationWrapper.insertBefore(icon, validationWrapper.firstChild);
        }

        icon.classList.remove('hidden');
      };

      if (!isValid) {
        updateValidationState(false);
        setHolderNameValid(false);
      } else if (isValid && !holderNameValid) {
        updateValidationState(true);
        setHolderNameValid(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateHolderName, holderNameValid, setHolderNameValid],
  );

  /**
   * Handles validation errors from the Adyen form
   */
  function onError() {
    dispatch(setError(200, 'check_the_fields_in_red', 'warning', false, null, null, null, 'fixed'));
  }

  const onSubmit = useCallback(
    ({ isValid, data }) => {
      if (!isValid || requestedPayment.current) return;
      const { browserInfo, paymentMethod } = data;

      if (!validateHolderName(paymentMethod.holderName)) {
        setHolderNameValid(false);
        dispatch(
          setError(200, 'card_holder_name_error', 'warning', false, null, null, null, 'fixed'),
        );
        return;
      }

      const adyenToken = base32.encode(
        [
          paymentMethod.encryptedCardNumber,
          paymentMethod.encryptedExpiryMonth,
          paymentMethod.encryptedExpiryYear,
          paymentMethod.encryptedSecurityCode,
        ].join(':'),
      );

      const mask = firstDigits.toString().length === 8 ? 'xxxx' : 'xxxxxx';

      const cardValues = {
        holderName: paymentMethod.holderName,
        cardNumber: `${firstDigits}${mask}${lastDigits}`,
        cardBrand,
        monthlySelectedPlan,
      };

      const paymentInfo = {
        adyenToken,
        amount: purchaseTotal,
        currency,
        browserInfo,
      };

      dispatch(cardPaymentAttempt(purchaseToken, cardValues, paymentInfo));
      requestedPayment.current = true;
    },
    [
      cardBrand,
      dispatch,
      firstDigits,
      lastDigits,
      monthlySelectedPlan,
      purchaseToken,
      currency,
      purchaseTotal,
      validateHolderName,
    ],
  );

  // Keep the latest callbacks/values in refs so the mount effect below does not need
  // them in its dependency array. onSubmit and handleHolderNameValidation change their
  // reference on every keystroke (onSubmit depends on firstDigits/lastDigits/cardBrand,
  // handleHolderNameValidation on holderNameValid). If they were dependencies, the effect
  // would re-run and remount the Adyen card component on each keystroke.
  const onSubmitRef = useRef(onSubmit);
  const handleHolderNameValidationRef = useRef(handleHolderNameValidation);
  const purchaseTotalRef = useRef(purchaseTotal);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
    handleHolderNameValidationRef.current = handleHolderNameValidation;
    purchaseTotalRef.current = purchaseTotal;
  });

  useEffect(() => {
    const adyenInstance = adyen.getAdyen();

    if (!adyenElement.current) return undefined;
    if (!adyenInstance) return undefined;
    if (selectedPaymentOption !== 'credit_card') return undefined;
    if (!showAdyenForm) return undefined;

    // Resolve brand tokens at mount time. Adyen secured fields run in iframes and
    // cannot read CSS variables from the parent page, so `var(--…)` would not work
    // if passed as a string. Note: Adyen only supports system fonts inside those
    // iframes — custom @font-face fonts (e.g. Montserrat) will fall back.
    const rootStyles = getComputedStyle(document.documentElement);
    const resolveCssValue = (...propertyNames) => {
      for (let i = 0; i < propertyNames.length; i += 1) {
        const value = rootStyles.getPropertyValue(propertyNames[i]).trim();
        if (value && value !== 'undefined') return value;
      }
      return '';
    };
    const principalFont = resolveCssValue('--principal-font');
    const errorColor = resolveCssValue('--color-error-strong', '--color-error') || '#ff040d';

    const cardComponent = adyenInstance
      .create('card', {
        hasHolderName: true,
        holderNameRequired: true,
        showPayButton: false,
        amount: {
          value: purchaseTotalRef.current * 100,
          currency: 'MXN',
        },
        styles: {
          base: {
            ...(principalFont && { fontFamily: principalFont }),
          },
          error: {
            color: errorColor,
            border: `1px solid ${errorColor}`,
            borderColor: errorColor,
          },
          invalid: {
            color: errorColor,
            border: `1px solid ${errorColor}`,
            borderColor: errorColor,
          },
          validated: {
            color: '#66ba5b',
          },
          placeholder: {
            color: '#9B9B9B',
            fontWeight: '400',
            ...(principalFont && { fontFamily: principalFont }),
          },
        },
        onChange: (event) => {
          if (event.data.paymentMethod?.holderName) {
            handleHolderNameValidationRef.current(event.data.paymentMethod.holderName);
          }
        },
        onSubmit: (args) => onSubmitRef.current(args),
        onFieldValid,
        onBinValue,
        onBrand,
        onError,
      })
      .mount(adyenElement.current);
    cardComponentRef.current = cardComponent;

    // Remove the card component from the AdyenCheckout instance on cleanup.
    // AdyenCheckout.update() iterates over all registered components, so any
    // stale (detached) component left behind would throw
    // "Component is not mounted." on subsequent updates.
    return () => {
      try {
        if (cardComponentRef.current && typeof cardComponentRef.current.remove === 'function') {
          cardComponentRef.current.remove();
        }
      } catch (e) {
        // Swallow: cleanup must not throw inside React's effect teardown.
      }
      cardComponentRef.current = null;
    };
    // The effect intentionally mounts the Adyen card once per (element, option, form)
    // change. onSubmit/handleHolderNameValidation/purchaseTotal are read via refs so their
    // per-render reference changes don't remount the component; the amount stays in sync via
    // the cardComponent.update() effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adyenElement, selectedPaymentOption, showAdyenForm]);

  // Keep the card component's amount in sync with the current total/currency.
  // We intentionally call update() on the card component directly (not on the
  // AdyenCheckout instance) so it only touches our component, avoiding
  // crashes from any unrelated stale components registered globally.
  useEffect(() => {
    const cardComponent = cardComponentRef.current;
    if (!cardComponent || typeof cardComponent.update !== 'function') return;
    cardComponent.update({
      amount: {
        value: purchaseTotal * 100,
        currency: env.currency,
      },
    });
  }, [env.currency, purchaseTotal]);
}

export default useAdyen;
